/**
 * PRODUTO-V1 N7 — modal de edição de notas em foco.
 *
 * Backend já tem PUT /api/notes/{id} (edita content/title/type/tags/pinned).
 * Esta é uma camada de UI sobre ele.
 *
 * Features:
 *   - Edita type (chips clicáveis), title (input), content (textarea
 *     auto-resize), tags (chips removíveis)
 *   - Toggle "Pré-visualizar" → renderiza com NoteMarkdown (reusa
 *     o renderer do NOTES-V2 N3, XSS-safe)
 *   - Auto-save no fechar quando houve mudança + botão Salvar explícito
 *   - Esc fecha (descartando se nada mudou; warning se mudou)
 *   - Foco inicial no textarea
 *
 * Telemetria via executionTrace.js:
 *   notes.editor.opened     {type, has_content}
 *   notes.editor.saved      {type, content_changed, tags_count}
 *   notes.editor.cancelled  {}
 *
 * PII: traces sem content/title plain — só counts e flags.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, EyeOff, Save, Tag, Pin } from "lucide-react";
import { NoteMarkdown } from "@/utils/notesMarkdown";
import { NOTE_TYPES } from "@/utils/noteGrouping";
import trace from "@/services/executionTrace";

const TYPE_KEYS = ["idea", "journal", "vent", "research", "general"];

export default function NoteEditorModal({
  note,
  isOpen,
  onClose,
  onSave,        // async (changes) => bool — true se persistiu OK
}) {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("general");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [pinned, setPinned] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  // Carrega note no open. Reset quando fecha (próxima abertura limpa).
  useEffect(() => {
    if (!isOpen || !note) return;
    setContent(note.content || "");
    setTitle(note.title || "");
    setType(note.type && NOTE_TYPES[note.type] ? note.type : "general");
    setTags(Array.isArray(note.tags) ? [...note.tags] : []);
    setTagInput("");
    setPinned(!!note.pinned);
    setShowPreview(false);
    try {
      trace.record("notes.editor.opened", {
        type: note.type || "general",
        has_content: !!(note.content && note.content.length > 0),
      });
    } catch {/* */}
  }, [isOpen, note?.id, note?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Foco inicial no textarea (após carregar state).
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const t = setTimeout(() => textareaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Detecta mudanças vs note original.
  const hasChanges = useCallback(() => {
    if (!note) return false;
    if (content !== (note.content || "")) return true;
    if (title !== (note.title || "")) return true;
    if (type !== (note.type || "general")) return true;
    const origTags = Array.isArray(note.tags) ? note.tags : [];
    if (tags.length !== origTags.length) return true;
    if (tags.some((t, i) => t !== origTags[i])) return true;
    if (pinned !== !!note.pinned) return true;
    return false;
  }, [note, content, title, type, tags, pinned]);

  const buildChanges = () => {
    const changes = {};
    if (content !== (note?.content || "")) changes.content = content;
    if (title !== (note?.title || "")) changes.title = title || null;
    if (type !== (note?.type || "general")) changes.type = type;
    const origTags = Array.isArray(note?.tags) ? note.tags : [];
    if (tags.length !== origTags.length || tags.some((t, i) => t !== origTags[i])) {
      changes.tags = tags;
    }
    if (pinned !== !!note?.pinned) changes.pinned = pinned;
    return changes;
  };

  const handleSave = useCallback(async () => {
    if (!note || !hasChanges() || saving) {
      // Sem mudanças → fecha sem PUT.
      if (!hasChanges()) onClose?.();
      return;
    }
    setSaving(true);
    const changes = buildChanges();
    try {
      const ok = await onSave?.(changes);
      if (ok) {
        try {
          trace.record("notes.editor.saved", {
            type,
            content_changed: "content" in changes,
            tags_count: tags.length,
          });
        } catch {/* */}
        onClose?.();
      }
    } finally {
      setSaving(false);
    }
  }, [note, hasChanges, saving, type, tags, onSave, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = useCallback(() => {
    if (hasChanges()) {
      const ok = window.confirm("Descartar alterações?");
      if (!ok) return;
    }
    try { trace.record("notes.editor.cancelled", {}); } catch {/* */}
    onClose?.();
  }, [hasChanges, onClose]);

  // Esc fecha.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, handleCancel]);

  const addTag = () => {
    const v = (tagInput || "").trim();
    if (!v) return;
    if (tags.includes(v)) {
      setTagInput("");
      return;
    }
    setTags([...tags, v]);
    setTagInput("");
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  if (!isOpen || !note) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => {
          // Click no backdrop → cancel (com confirm se mudou).
          if (e.target === e.currentTarget) handleCancel();
        }}
        data-testid="note-editor-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.18 }}
          className="glass-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Editor de nota"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 flex-wrap" data-testid="editor-type-chips">
              {TYPE_KEYS.map((k) => {
                const cfg = NOTE_TYPES[k];
                if (!cfg) return null;
                const active = type === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setType(k)}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium uppercase tracking-wider transition-colors border ${
                      active ? "" : "border-transparent text-slate-500 hover:text-slate-300"
                    }`}
                    style={active ? {
                      backgroundColor: `${cfg.color}18`,
                      color: cfg.color,
                      borderColor: `${cfg.color}40`,
                    } : undefined}
                    data-testid={`editor-type-${k}`}
                  >
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPinned((p) => !p)}
                className={`p-2 rounded-lg transition-colors ${
                  pinned
                    ? "text-amber-400 bg-amber-500/10"
                    : "text-slate-500 hover:text-amber-400"
                }`}
                aria-pressed={pinned}
                aria-label={pinned ? "Desafixar" : "Fixar"}
                data-testid="editor-pin-toggle"
              >
                <Pin className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-300 transition-colors"
                aria-label="Fechar"
                data-testid="editor-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título (opcional)"
              className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors text-base"
              data-testid="editor-title"
            />

            {showPreview ? (
              <div
                className="min-h-[200px] px-3 py-3 bg-white/[0.02] border border-white/[0.04] rounded-lg"
                data-testid="editor-preview"
              >
                {content ? (
                  <NoteMarkdown content={content} clamp={0} />
                ) : (
                  <p className="text-sm text-slate-600 italic">Nada pra pré-visualizar ainda.</p>
                )}
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="O que está na sua mente?"
                rows={12}
                className="w-full px-3 py-3 bg-white/[0.03] border border-white/[0.06] rounded-lg text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors text-base leading-relaxed resize-y min-h-[200px]"
                data-testid="editor-content"
              />
            )}

            {/* Tags */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Tag className="w-3.5 h-3.5" />
                Tags
              </div>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="editor-tags">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                    data-testid={`editor-tag-${t}`}
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="hover:text-red-400 transition-colors"
                      aria-label={`Remover tag ${t}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onBlur={addTag}
                  placeholder="Adicionar..."
                  className="flex-1 min-w-[120px] px-2 py-0.5 bg-transparent border-b border-white/[0.06] text-xs text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors"
                  data-testid="editor-tag-input"
                />
              </div>
            </div>
          </div>

          {/* Footer. FaseA T3: padding-bottom = max(1rem, safe-area) pra
              clearar home indicator iOS sem reduzir o py-4 do desktop. */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] border-t border-white/[0.04]">
            <button
              type="button"
              onClick={() => setShowPreview((p) => !p)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-cyan-400 transition-colors"
              data-testid="editor-preview-toggle"
            >
              {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showPreview ? "Editar" : "Pré-visualizar"}
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-xl text-sm font-medium text-slate-400 hover:text-white transition-colors"
                data-testid="editor-cancel"
              >
                Cancelar
              </button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                disabled={saving || !hasChanges()}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-medium bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                data-testid="editor-save"
              >
                <Save className="w-4 h-4" />
                {saving ? "Salvando..." : "Salvar"}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
